import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/authStore';
import { MessageSquare, Send, User, Sparkles } from 'lucide-react';

export const AdminMessagesPage: React.FC = () => {
  const { user } = useAuth();
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');

  const queryClient = useQueryClient();

  const { data: threadsRes, isLoading } = useQuery({
    queryKey: ['admin-messages'],
    queryFn: () => api.get('/admin/messages').then((res) => res.data.data),
  });

  const threads = threadsRes || [];
  const activeThread = threads.find((t: any) => t.id === selectedThreadId) || threads[0] || null;

  const replyMutation = useMutation({
    mutationFn: async ({ threadId, content }: { threadId: string; content: string }) => {
      return api.post('/admin/messages/reply', { threadId, content });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-messages'] });
      setReplyContent('');
    },
  });

  const handleSendReply = (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyContent.trim() || !activeThread) return;
    replyMutation.mutate({
      threadId: activeThread.id,
      content: replyContent.trim(),
    });
  };

  return (
    <div className="space-y-6 font-sans text-slate-900 antialiased">
      
      {/* 1. Header with High Visibility */}
      <div className="bg-white rounded-2xl md:rounded-3xl p-6 shadow-sm border border-slate-200/90 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-brand-700 font-mono uppercase tracking-widest font-bold">
              COMMUNICATIONS HUB
            </span>
            <span className="text-slate-400 font-mono text-xs">&bull;</span>
            <span className="text-xs text-slate-600 font-serif italic">
              FOR THE TRUTH PRESS PRIVATE LIMITED
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-serif font-black text-slate-900 flex items-center gap-2.5 mt-1">
            <MessageSquare className="w-7 h-7 text-[#FF6B4A]" />
            <span>Client Communication Center</span>
          </h1>
          <p className="text-xs sm:text-sm text-slate-600 font-medium mt-1">
            Direct threaded dialogue with authors and contracted publishing partners.
          </p>
        </div>

        <div className="flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-slate-50 border border-slate-200 text-xs font-mono font-bold text-slate-700">
          <span>Active Threads: <strong className="text-slate-900">{threads.length}</strong></span>
        </div>
      </div>

      {/* 2. Messages Interface */}
      {isLoading ? (
        <div className="p-16 text-center text-xs font-mono text-slate-500 bg-white rounded-2xl border border-slate-200">
          Loading conversations...
        </div>
      ) : threads.length === 0 ? (
        <div className="p-16 rounded-2xl bg-white border border-slate-200 text-center space-y-3 shadow-sm">
          <MessageSquare className="w-12 h-12 text-slate-400 mx-auto" />
          <h3 className="font-serif text-lg font-bold text-slate-900">No Active Message Threads</h3>
          <p className="text-xs text-slate-500 font-medium">Client messages from active publishing projects will appear here.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 bg-white rounded-2xl md:rounded-3xl border border-slate-200/90 overflow-hidden min-h-[600px] shadow-sm">
          
          {/* Threads Sidebar */}
          <div className="lg:col-span-4 border-r border-slate-100 p-4 space-y-2 bg-slate-50/50">
            <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500 font-bold px-2 block">
              All Threads ({threads.length})
            </span>
            <div className="space-y-1">
              {threads.map((thr: any) => {
                const isSelected = activeThread?.id === thr.id;
                return (
                  <div
                    key={thr.id}
                    onClick={() => setSelectedThreadId(thr.id)}
                    className={`p-3.5 rounded-2xl cursor-pointer transition-all ${
                      isSelected
                        ? 'bg-white border-2 border-brand-500 shadow-sm'
                        : 'hover:bg-slate-100/80 border border-transparent'
                    }`}
                  >
                    <div className="flex items-center justify-between text-xs font-extrabold text-slate-900 truncate">
                      <span className="truncate pr-2">{thr.title}</span>
                      <span className="text-[10px] font-mono text-slate-500 font-bold shrink-0">
                        {new Date(thr.lastMessageAt).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="text-[11px] text-slate-600 font-semibold mt-0.5">
                      Client: {thr.client?.user?.firstName || thr.client?.firstName || 'Author'} {thr.client?.user?.lastName || thr.client?.lastName || ''}
                    </div>
                    {thr.project && (
                      <span className="text-[10px] font-mono text-brand-700 font-bold block mt-0.5">
                        [{thr.project.projectCode}] {thr.project.title}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Chat Stream */}
          <div className="lg:col-span-8 flex flex-col justify-between p-6">
            <div className="border-b border-slate-100 pb-4">
              <h3 className="font-serif text-lg font-extrabold text-slate-900">{activeThread?.title}</h3>
              <p className="text-[11px] text-slate-600 font-mono font-semibold">
                Client: {activeThread?.client?.user?.firstName || activeThread?.client?.firstName || 'Author'} {activeThread?.client?.user?.lastName || activeThread?.client?.lastName || ''} &bull; {activeThread?.client?.companyName || 'Author Portal'}
              </p>
            </div>

            <div className="flex-1 overflow-y-auto py-6 space-y-4 max-h-[420px] pr-2">
              {activeThread?.messages?.map((msg: any) => {
                const isStaff = msg.senderId === user?.id;
                return (
                  <div
                    key={msg.id}
                    className={`flex flex-col ${isStaff ? 'items-end' : 'items-start'}`}
                  >
                    <div
                      className={`max-w-md p-4 rounded-2xl text-xs sm:text-sm leading-relaxed shadow-xs ${
                        isStaff
                          ? 'bg-slate-900 text-white rounded-br-none'
                          : 'bg-slate-100 text-slate-900 border border-slate-200 rounded-bl-none'
                      }`}
                    >
                      <span className={`text-[10px] font-bold block mb-1 font-mono ${isStaff ? 'text-amber-300' : 'text-brand-700'}`}>
                        {isStaff ? 'Editorial Staff (You)' : `${activeThread?.client?.user?.firstName || activeThread?.client?.firstName || 'Author'}`}
                      </span>
                      <p className="whitespace-pre-wrap font-medium">{msg.content}</p>
                    </div>
                    <span className="text-[10px] text-slate-500 font-mono font-bold mt-1 px-1">
                      {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                );
              })}
            </div>

            <form onSubmit={handleSendReply} className="pt-4 border-t border-slate-100 flex items-center gap-2">
              <input
                type="text"
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                placeholder="Write an official editorial reply to the client..."
                className="flex-1 px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 font-semibold text-xs focus:ring-2 focus:ring-brand-500 focus:outline-hidden focus:bg-white transition-all"
              />
              <button
                type="submit"
                disabled={!replyContent.trim() || replyMutation.isPending}
                className="p-3 px-5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-extrabold text-xs disabled:opacity-50 transition-colors shrink-0 flex items-center gap-1.5 cursor-pointer shadow-sm"
              >
                <span>Send</span>
                <Send className="w-3.5 h-3.5 text-[#FF6B4A]" />
              </button>
            </form>
          </div>

        </div>
      )}

    </div>
  );
};
